import SelfCareCalendarModal from 'components/global/CalendarModal/forks/SelfcareCalendarModal'
import OpeningHoursModal from 'components/global/OpeningHoursModal'
import Box from 'components/shared/Box'
import Datepicker from 'components/shared/Datepicker'
import Select from 'components/shared/Select'
import Info from 'components/shared/Info'
import Text from 'components/shared/Text'
import { useEffect, useMemo, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import EditorSummary from './EditorSummary'
import styled from 'styled-components'
import { respondAbove } from 'styles/mediaQueries'
import { Person, Time, Zone } from 'store/api-slice/types'
import { useApp } from 'store/steps'
import {
    useListTicketPersonsQuery,
    useListTicketPriceByTypesQuery,
    useListTicketZonesByPersonQuery,
} from 'store/api-slice/hooks'
import { useAppSelector } from 'store/hooks'
import { useSelfcare } from 'store/selfcare-slice/hooks'
import { TicketReservation } from 'store/selfcare-slice/types'

interface TicketEditorProps {
    ticket: TicketReservation
    onCancel: () => void
    index: number
}

const TicketEditor = ({ ticket, onCancel, index }: TicketEditorProps) => {
    const { language } = useApp()
    const { formatMessage } = useIntl()
    const { editItem, isEditProcessing } = useSelfcare()

    const { original, changes } = ticket
    const dataToDisplay = changes || original
    const { detail } = dataToDisplay || {}
    const { person, ticketTime, zone, day, orderTicketId } = detail || {}

    const [newDate, setNewDate] = useState<string>(day)
    const [guestType, setGuestType] = useState<Person>(person)
    const [zoneType, setZoneType] = useState<Zone | null>(zone)
    const [ticketType, setTicketType] = useState<Time | null>(ticketTime)
    const [newPrice, setNewPrice] = useState<number | null>(null)

    const [isCalendarOpened, setIsCalendarOpened] = useState(false)
    const [isHoursModalOpened, setIsHoursModalOpened] = useState(false)

    const { transport, cabanas } = useAppSelector((state) => state.selfcare)

    const originalDate = day
    const originalZoneType = zone
    const originalGuestType = person
    const originalTicketType = ticketTime

    const isOriginalDateChanged = newDate !== originalDate
    const isOriginalGuestTypeChanged = guestType !== originalGuestType
    const isOriginalZoneTypeChanged = zoneType !== originalZoneType
    const isOriginalTicketTypeChanged = ticketType !== originalTicketType
    const isDataChangedAndCompleted =
        (isOriginalDateChanged ||
            isOriginalZoneTypeChanged ||
            isOriginalGuestTypeChanged ||
            isOriginalTicketTypeChanged) &&
        !!zoneType &&
        !!ticketType
    const isBusTicketInReservation = transport && transport.length > 0
    const isCabanaInReservation = cabanas && cabanas.length > 0

    const hasPotentiallyConnectedItemsWarning =
        isDataChangedAndCompleted &&
        (isBusTicketInReservation || isCabanaInReservation)

    const { data: personTypes, isFetching: arePersonTypesFetching } =
        useListTicketPersonsQuery({
            _locale: language,
        })

    const personTypesOptions = personTypes?.persons?.map(
        ({ title, description, key }) => ({
            option: `${title} ${description ? description.toLowerCase() : ''}`,
            value: key,
        })
    )

    const { data: zoneTypes, isFetching: areZoneTypesFetching } =
        useListTicketZonesByPersonQuery(
            {
                _locale: language,
                person: guestType,
            },
            { refetchOnMountOrArgChange: true }
        )

    const zoneTypesOptions = zoneTypes?.zones?.map(({ key, title }) => ({
        option: `${title}`,
        value: key,
    }))

    const { data: ticketTypes, isFetching: areTicketTypesFetching } =
        useListTicketPriceByTypesQuery(
            {
                _locale: language,
                person: guestType,
                startDate: newDate,
                zone: zoneType,
            },
            {
                refetchOnMountOrArgChange: true,
                skip: !zoneType || !newDate,
            }
        )

    const ticketTypesOptions = useMemo(
        () =>
            ticketTypes?.tickets?.map(({ title, time, status, prices }) => {
                // TODO: simplify after backend changes in fields descriptions
                const displayTitle = title || ''
                const displayNamePattern = /["„](.+)["”]/
                const displayNameMatch = displayTitle.match(displayNamePattern)

                const { active: isTypeActive } = status || {}

                return {
                    option: displayNameMatch
                        ? displayNameMatch[1]
                        : displayTitle,
                    value: time,
                    isDisabled: !isTypeActive || !prices?.length,
                }
            }),
        [ticketTypes]
    )

    const setDate = (date: string) => {
        setNewDate(date)
        setNewPrice(null)
    }
    const setGuest = (value: string) => {
        setGuestType(value as Person)
        setZone(null)
        setTime(null)
        setNewPrice(null)
    }
    const setZone = (value: string | null) => {
        setZoneType(value ? (value as Zone) : null)
        setTime(null)
        setNewPrice(null)
    }
    const setTime = (value: string | null) => {
        setTicketType(value ? (value as Time) : null)
    }

    useEffect(() => {
        if (!ticketType || !ticketTypes || !isDataChangedAndCompleted) return

        const price = ticketTypes?.tickets?.find(
            (type) => type.time === ticketType
        )?.totalPrice

        price && setNewPrice(price)
    }, [ticketType, ticketTypes, isDataChangedAndCompleted])

    useEffect(() => {
        const isCurrentTicketTypeUnavailable = ticketTypesOptions?.find(
            ({ value, isDisabled }) => value === ticketType && isDisabled
        )

        if (isCurrentTicketTypeUnavailable) setTime(null)
    }, [ticketTypesOptions])

    return (
        <Box isFullWidth gap={16} mt={[0, 8]}>
            <Text as="h4" variant="heading4">
                <FormattedMessage
                    id="selfcare.tickets.ticket_change"
                    defaultMessage="Zmiana Biletu"
                />
            </Text>
            <CustomWrapper isFullWidth>
                <Datepicker
                    openCalendar={() => setIsCalendarOpened(true)}
                    id="edit-ticket-date"
                    value={[newDate]}
                    label={formatMessage({
                        id: 'global.calendar.change_ticket_label',
                        defaultMessage: 'Data',
                    })}
                    hasNoMaxWidth
                    testId={`selfcare-ticket-${index}-trigger-stay-date`}
                />
            </CustomWrapper>
            <Box
                gap={[16, 8]}
                isFullWidth
                direction={['column', 'row']}
                mb={[0, 16]}
            >
                <Box minWidth={[0, 324]} isFullWidth>
                    <Select
                        id="edit-ticket-guest_type"
                        value={guestType}
                        onChange={setGuest}
                        options={
                            arePersonTypesFetching
                                ? null
                                : personTypesOptions || null
                        }
                        label={formatMessage({
                            id: 'tickets.edit_guest_type_label',
                            defaultMessage: 'Typ gościa',
                        })}
                        hasNoMaxWidth
                        placeholder={formatMessage({
                            id: 'tickets.person_section_caption',
                            defaultMessage: 'Wybierz typ gościa',
                        })}
                        isLoading={arePersonTypesFetching}
                        isDisabled={arePersonTypesFetching}
                        testId={`selfcare-ticket-${index}-person-select`}
                    />
                </Box>

                <Select
                    id="edit-ticket-zone"
                    value={zoneType}
                    onChange={setZone}
                    options={
                        areZoneTypesFetching ? null : zoneTypesOptions || null
                    }
                    label={formatMessage({
                        id: 'tickets.edit_zones_label',
                        defaultMessage: 'Strefy',
                    })}
                    hasNoMaxWidth
                    placeholder={formatMessage({
                        id: 'tickets.zones_section_caption',
                        defaultMessage: 'Wybierz strefy',
                    })}
                    isLoading={areZoneTypesFetching}
                    isDisabled={areZoneTypesFetching}
                    testId={`selfcare-ticket-${index}-zone-select`}
                />

                <Select
                    id="edit-ticket-type"
                    value={ticketType}
                    onChange={setTime}
                    options={
                        areTicketTypesFetching
                            ? null
                            : ticketTypesOptions || null
                    }
                    label={formatMessage({
                        id: 'tickets.edit_ticket_type_label',
                        defaultMessage: 'Rodzaj biletu',
                    })}
                    hasNoMaxWidth
                    placeholder={formatMessage({
                        id: 'tickets.type_section_caption',
                        defaultMessage: 'Wybierz rodzaj biletu',
                    })}
                    isLoading={areTicketTypesFetching}
                    isDisabled={!zoneType || areTicketTypesFetching}
                    testId={`selfcare-ticket-${index}-type-select`}
                />
            </Box>
            {hasPotentiallyConnectedItemsWarning && (
                <Box mb={[0, 16]}>
                    <Info color="info_warning" hasBorder hasNoBackground>
                        <FormattedMessage
                            id="selfcare.ticket_and_upsell_conflict_warning"
                            defaultMessage="Masz wykupione dodatkowe usługi. Upewnij się, czy któreś z nich nie wymagają zmiany. Możesz je edytować po zapisaniu zmian w tym bilecie."
                        />
                    </Info>
                </Box>
            )}

            <EditorSummary
                handleCancel={onCancel}
                updatedTicket={{
                    productType: 'ticket',
                    detail:
                        ticketType && zoneType
                            ? {
                                  day: newDate,
                                  person: guestType,
                                  ticketTime: ticketType,
                                  zone: zoneType,
                                  orderTicketId,
                              }
                            : null,
                }}
                currentPrice={dataToDisplay.price || 0}
                newPrice={newPrice || null}
                isSavingDisabled={!isDataChangedAndCompleted}
                editItem={editItem}
                isFetching={isEditProcessing}
                index={index}
                testIdPrefix="selfcare-ticket"
            />
            <SelfCareCalendarModal
                isOpened={isCalendarOpened}
                close={() => setIsCalendarOpened(false)}
                openHoursModal={() => setIsHoursModalOpened(true)}
                customConfirmDate={setDate}
                currentDate={newDate}
                testIdPrefix="selfcare-ticket"
            />
            <OpeningHoursModal
                isOpened={isHoursModalOpened}
                close={() => setIsHoursModalOpened(false)}
            />
        </Box>
    )
}

export default TicketEditor

const CustomWrapper = styled(Box)`
    @media ${respondAbove.xl} {
        max-width: 230px;
    }
`
